import React, { useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon, type IconName } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { useT, type LabelKey } from '@/i18n/labels';
import { useConversationStore } from '@/stores/conversationStore';
import { usePermission } from '@/hooks/usePermission';
import { usePersistedFlag } from '@/hooks/usePersistedFlag';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useFolderCounts } from '@/console/useFolderCounts';
import {
  FOLDER_SECTION_LABEL,
  categoryFolderId,
  folderRule,
  tagFolderId,
  visibleSystemFolders,
  type FolderSection,
} from '@/console/folders';
import type { FolderId } from '@/types';

/** localStorage key of the agent's own expanded/collapsed choice (a per-viewer convenience, like the contact panel's). */
export const FOLDERS_COLLAPSED_KEY = 'ac-folders-collapsed';
/** Below this viewport width the rail folds itself to icons. */
export const RAIL_AUTO_COLLAPSE_QUERY = '(max-width: 1359.98px)';

interface Item {
  id: FolderId;
  label: string;
  rule?: string | null;
  icon: IconName;
  /** A category's colour dot. */
  color?: string;
  isTag?: boolean;
  /** The folder exists only for some viewers (a folder with `requires`): drawn with a shield. */
  restricted?: boolean;
}

interface FolderRailProps {
  selected: FolderId;
  onSelect: (id: FolderId) => void;
}

/** A hex colour from the tenant's definitions reaches inline style: anything else is dropped. */
const HEX = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const safeColor = (c: string | undefined | null): string | undefined => (c && HEX.test(c.trim()) ? c.trim() : undefined);

/**
 * The folder rail (ADR-033): My work, Team, Tags, More. Team is "All open" FIRST, then one folder per ACTIVE conversation
 * status in the tenant's order - the closed (isSystem) status included - each `cat:<key>` with its colour dot (the
 * separate "Conversation Status" section is gone; its items live in Team). The counts are the SERVER's
 * (`useFolderCounts`), and there is NO create button on the statuses or Tags: the console moves and attaches, the
 * Admin Portal defines (D8). The tenant may hide the Tags section (console settings `hideTagFolders`, PR-A).
 *
 * Collapsed: icons only, each one named by a tooltip (hover and keyboard focus) and by its accessible name. The rail
 * folds itself below 1360px; that fold is automatic and never overwrites the agent's own choice, which is remembered
 * in localStorage and applies on a wide screen. Up/Down/Home/End move between folders; Enter or Space selects.
 */
export const FolderRail: React.FC<FolderRailProps> = ({ selected, onSelect }) => {
  const { t, lang } = useT();
  const { roles } = usePermission();
  const { countOf } = useFolderCounts();
  const stateDefs = useConversationStore((s) => s.stateDefs);
  const tagCatalog = useConversationStore((s) => s.tagCatalog);
  // PR-A: the tenant may hide the Tags SECTION of the rail (a display preference; tags stay usable on conversations).
  // Only an explicit `true` from the server hides it: settings not known yet, or a failed read, show the tags.
  const hideTagFolders = useConversationStore((s) => s.consoleSettings?.hideTagFolders === true);

  const [chosenCollapsed, toggleChosen] = usePersistedFlag(FOLDERS_COLLAPSED_KEY, false);
  const narrow = useMediaQuery(RAIL_AUTO_COLLAPSE_QUERY);
  // The agent may open the folded rail while the window is narrow; that is for this visit only and resets when the
  // window narrows again. The remembered choice is never touched by it.
  const [openWhileNarrow, setOpenWhileNarrow] = useState(false);
  const wasNarrowRef = useRef(narrow);
  if (wasNarrowRef.current !== narrow) {
    wasNarrowRef.current = narrow;
    if (narrow) setOpenWhileNarrow(false);
  }
  const collapsed = narrow ? !openWhileNarrow : chosenCollapsed;
  const toggle = () => (narrow ? setOpenWhileNarrow((v) => !v) : toggleChosen());

  const navRef = useRef<HTMLElement>(null);

  const systemItems = (section: FolderSection): Item[] =>
    visibleSystemFolders(roles)
      .filter((f) => f.section === section)
      .map((f) => ({
        id: f.id,
        label: t(f.labelKey),
        rule: folderRule(f.id, t),
        icon: f.icon,
        restricted: !!f.requires,
      }));

  const categoryItems = useMemo<Item[]>(
    () =>
      (Array.isArray(stateDefs) ? stateDefs : [])
        .filter((d) => d.isActive !== false)
        .sort((a, b) => a.order - b.order)
        .map((d) => ({
          id: categoryFolderId(d.key),
          label: d.label?.[lang] || d.key,
          icon: 'folder' as IconName,
          color: safeColor(d.color),
        })),
    [stateDefs, lang],
  );

  const tagItems = useMemo<Item[]>(
    () =>
      (Array.isArray(tagCatalog) ? tagCatalog : [])
        .filter((g) => g.isActive)
        .map((g) => ({
          id: tagFolderId(g.key),
          label: g.label,
          icon: 'tag' as IconName,
          color: safeColor(g.color),
          isTag: true,
        })),
    [tagCatalog],
  );

  const renderItem = (it: Item) => {
    const n = countOf(it.id);
    const countText = n === null ? '–' : String(n);
    const active = selected === it.id;
    const tipLabel = `${it.isTag ? '# ' : ''}${it.label} · ${countText}`;
    return (
      <li key={it.id}>
        <Tooltip label={tipLabel} sub={it.rule ?? undefined} block>
          <button
            type="button"
            className={'fr-item' + (active ? ' active' : '') + (it.isTag ? ' is-tag' : '')}
            onClick={() => onSelect(it.id)}
            aria-current={active ? 'page' : undefined}
            aria-label={`${it.label}, ${countText}`}
            data-testid={`folder-${it.id}`}
          >
            <span className="fr-icon" style={it.color && it.isTag ? { color: it.color } : undefined}>
              <Icon name={it.icon} size={18} />
              {it.color && !it.isTag && <span className="fr-dot" style={{ background: it.color }} />}
            </span>
            <span className="fr-label" dir="auto">
              {it.label}
            </span>
            {it.restricted && <Icon name="shield" size={13} className="fr-restricted" title={t('fr_restricted')} />}
            <span
              className={'fr-count' + (n === 0 ? ' zero' : '')}
              data-testid={`folder-count-${it.id}`}
            >
              {countText}
            </span>
          </button>
        </Tooltip>
      </li>
    );
  };

  /** Up / Down / Home / End move focus between the folder buttons; the buttons themselves already take Enter and Space. */
  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    const nav = navRef.current;
    if (!nav) return;
    const buttons = Array.from(nav.querySelectorAll<HTMLButtonElement>('button.fr-item'));
    if (buttons.length === 0) return;
    const at = buttons.findIndex((b) => b === document.activeElement);
    let next = at;
    if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = buttons.length - 1;
    else if (e.key === 'ArrowDown') next = at < 0 ? 0 : Math.min(buttons.length - 1, at + 1);
    else next = at < 0 ? buttons.length - 1 : Math.max(0, at - 1);
    e.preventDefault();
    buttons[next]?.focus();
  };

  const sectionTitle = (section: FolderSection): string => t(FOLDER_SECTION_LABEL[section] as LabelKey);
  const work = systemItems('work');
  const team = systemItems('team');
  const more = systemItems('more');

  const toggleLabel = collapsed ? t('fr_expand') : t('fr_collapse');

  return (
    <nav
      ref={navRef}
      className={'fr' + (collapsed ? ' collapsed' : '')}
      aria-label={t('fr_title')}
      data-testid="folder-rail"
      data-collapsed={collapsed ? 'true' : 'false'}
      onKeyDown={onKeyDown}
    >
      <div className="fr-head">
        {/* No visible title (operator 2026-10-12: "Help Desk" took a row for nothing); the panel keeps its accessible name. */}
        <Tooltip label={toggleLabel}>
          <button
            type="button"
            className="ac-icon-btn"
            onClick={toggle}
            aria-label={toggleLabel}
            aria-expanded={!collapsed}
            data-testid="fr-toggle"
          >
            <Icon name={collapsed ? 'chevronEnd' : 'chevronStart'} size={18} />
          </button>
        </Tooltip>
      </div>

      <div className="fr-scroll">
        <Section title={sectionTitle('work')} collapsed={collapsed} testId="fr-sec-work">
          {work.map(renderItem)}
        </Section>
        <Section title={sectionTitle('team')} collapsed={collapsed} testId="fr-sec-team">
          {team.map(renderItem)}
          {categoryItems.map(renderItem)}
        </Section>
        {!hideTagFolders && (
          <Section title={t('fr_sec_tags')} collapsed={collapsed} testId="fr-sec-tags">
            {tagItems.length > 0 ? (
              tagItems.map(renderItem)
            ) : (
              !collapsed && (
                <li className="fr-empty" data-testid="fr-no-tags">
                  {t('fr_no_tags')}
                </li>
              )
            )}
          </Section>
        )}
        <Section title={sectionTitle('more')} collapsed={collapsed} testId="fr-sec-more">
          {more.map(renderItem)}
        </Section>
      </div>
    </nav>
  );
};

function Section({
  title,
  collapsed,
  testId,
  children,
}: {
  title: string;
  collapsed: boolean;
  testId: string;
  children: ReactNode;
}) {
  return (
    <section className="fr-sec" data-testid={testId}>
      {collapsed ? (
        <div className="fr-sep" role="separator" aria-label={title} />
      ) : (
        <div className="fr-sec-head">
          <span>{title}</span>
        </div>
      )}
      <ul>{children}</ul>
    </section>
  );
}
